import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const ContentContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
};

export const ContentProvider = ({ children }) => {
  // Single in-memory map: { 'home.hero.title': 'overridden text', ... }
  const [content, setContentMap] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Admin-only "edit mode" — controls whether <EditableText> shows its pencil
  // icon next to text across the site. Persisted in localStorage so the
  // admin's preference survives page reloads. Off by default, even for admins.
  const [editMode, setEditModeState] = useState(() => {
    try {
      return localStorage.getItem('shaktiverse_edit_mode') === '1';
    } catch (_) {
      return false;
    }
  });

  const setEditMode = useCallback((next) => {
    setEditModeState(!!next);
    try {
      if (next) localStorage.setItem('shaktiverse_edit_mode', '1');
      else localStorage.removeItem('shaktiverse_edit_mode');
    } catch (_) { /* ignore quota */ }
  }, []);

  const toggleEditMode = useCallback(() => {
    setEditMode(!editMode);
  }, [editMode, setEditMode]);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/content`);
      setContentMap(res.data || {});
    } catch (e) {
      // Non-fatal — pages fall back to hardcoded defaults
      console.warn('Content load failed, using defaults', e?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Returns the override if present, else the provided default.
  const get = useCallback(
    (key, fallback = '') => {
      if (Object.prototype.hasOwnProperty.call(content, key)) return content[key];
      return fallback;
    },
    [content]
  );

  // Admin-only: save a new value and update local cache immediately.
  const save = useCallback(async (key, value, token) => {
    const res = await axios.put(
      `${API}/content/${encodeURIComponent(key)}`,
      { value },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setContentMap((m) => ({ ...m, [key]: res.data.value }));
    return res.data;
  }, []);

  // Admin-only: reset a key to its hardcoded default.
  const reset = useCallback(async (key, token) => {
    await axios.delete(`${API}/content/${encodeURIComponent(key)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setContentMap((m) => {
      const next = { ...m };
      delete next[key];
      return next;
    });
  }, []);

  return (
    <ContentContext.Provider value={{
      content, loaded, get, save, reset, refresh,
      editMode, setEditMode, toggleEditMode,
    }}>
      {children}
    </ContentContext.Provider>
  );
};
